// Event registry shared with the API server. Both sides validate against this,
// so any change here is a breaking change for the producer.
//
// Conventions: names are past tense, props carry no PII (no email, phone, name,
// card data or raw gateway payloads), and money is always
// { amount: number; currency: string } — never a string.

type Platform = 'web' | 'ios' | 'android' | 'server';

type EventSource = 'client' | 'server';

type Money = {
    amount: number;
    currency: string;
};

// ---------------------------------------------------------------------------
// Client events
// ---------------------------------------------------------------------------

type ClientEventMap = {
    'search.submitted': {
        regionId: number;
        regionName: string | null;
        checkin: string;                    // YYYY-MM-DD
        checkout: string;                   // YYYY-MM-DD
        nights: number;
        guests: number;                     // room count — guests array length
        adults: number;
        children: number;
        residency: string;                  // ISO 3166-1 alpha-2, lowercase (matches ETG)
        currency: string;
        /** checkin − today, in days. Precomputed so demand curves need no date maths. */
        leadTimeDays: number;
    };

    /**
     * resultCount 0 means we could not serve the destination at all. errorCode
     * separates an ETG refusal from genuine no-availability — serp/region is
     * capped at 10 req/min and the two look identical to the traveller.
     */
    'search.results_returned': {
        searchId: string;
        resultCount: number;
        catalogueCount: number | null;      // region's full mapped catalogue
        page: number;
        latencyMs: number;
        errorCode: string | null;
    };

    'search.filter_applied': {
        searchId: string;
        filterType: 'minStars' | 'minPrice' | 'maxPrice' | 'freeCancellation' | 'breakfast' | 'amenities';
        value: string;
        countBefore: number;
        countAfter: number;
    };

    'search.sort_changed': {
        searchId: string;
        sort: 'rank' | 'price' | 'stars';
    };

    'search.page_viewed': {
        searchId: string;
        page: number;
    };

    /** rank cannot be reconstructed after the fact — the search session expires. */
    'search.result_clicked': {
        searchId: string;
        hid: number;
        rank: number;
        page: number;
        minPrice: number;
        starRating: number;
        hasFreeCancellation: boolean;
        hasBreakfast: boolean;
    };

    'hotel.viewed': {
        hid: number;
        source: 'search' | 'cms' | 'direct';
        searchId: string | null;
        latencyMs: number | null;
    };

    'hotel.rooms_returned': {
        hid: number;
        roomCount: number;
        minPrice: number | null;
        errorCode: string | null;
    };

    /** bookHash ties the client journey to the server's payment record. */
    'hotel.room_selected': {
        hid: number;
        bookHash: string;
        roomName: string;
        mealName: string | null;
        total: Money;
        freeCancellationBefore: string | null;
    };

    'checkout.started': {
        bookHash: string;
        hid: number;
        total: Money;
    };

    'checkout.guest_details_completed': {
        bookHash: string;
        roomCount: number;
        secondsToComplete: number;
    };

    /** /booking/prebook already returns priceChanged; this records how often it costs us the sale. */
    'checkout.prebook_result': {
        bookHash: string;
        priceChanged: boolean;
        oldTotal: Money | null;
        newTotal: Money | null;
    };

    /** Sent via sendBeacon on unload. Also derived server-side as a cross-check. */
    'checkout.abandoned': {
        bookHash: string;
        lastStep: 'guest_details' | 'prebook' | 'payment_redirect';
        secondsInCheckout: number;
    };

    'cms.section_viewed': {
        sectionKey: string;
        itemCount: number;
    };

    'cms.item_clicked': {
        sectionKey: string;
        hid: number;
        position: number;
    };

    'screen.viewed': {
        screen: string;
        previousScreen: string | null;
    };

    'app.error': {
        message: string;
        screen: string | null;
        fatal: boolean;
    };
};

// ---------------------------------------------------------------------------
// Server events. Authoritative — where a client event describes the same fact,
// these win. Payment is confirmed by gateway webhook, never by the client.
// ---------------------------------------------------------------------------

type ServerEventMap = {
    'payment.initiated': {
        transactionId: string;
        partnerOrderId: string;
        gateway: 'payu' | 'stripe';
        country: string;                    // resolved from CDN headers, never raw IP
        total: Money;
        bookHash: string;
        hid: string | null;
        /** Mock rows live in the same collections as real ones and must never count as revenue. */
        isMock: boolean;
    };

    'payment.captured': {
        transactionId: string;
        partnerOrderId: string;
        gateway: 'payu' | 'stripe';
        total: Money;
        secondsFromInitiate: number;
    };

    'payment.failed': {
        transactionId: string;
        partnerOrderId: string;
        gateway: 'payu' | 'stripe';
        reason: string;
    };

    'payment.refunded': {
        transactionId: string;
        partnerOrderId: string;
        total: Money;
        reason: string | null;
    };

    'booking.order_placed': {
        partnerOrderId: string;
        orderId: number;
        hid: string | null;
        total: Money;
        secondsFromCapture: number;
        retryCount: number;
    };

    /**
     * Paired with a payment.captured on the same partnerOrderId this is the
     * money-taken-no-room case, which needs an alert rather than a report.
     */
    'booking.order_failed': {
        partnerOrderId: string;
        failureReason: string;
        retryCount: number;
    };

    'booking.cancelled': {
        partnerOrderId: string;
        orderId: number | null;
        refund: Money | null;
        initiatedBy: 'user' | 'admin';
        withinFreeWindow: boolean;
    };

    /** errorCode 'endpoint_exceeded_limit' is the serp/region rate-limit signal. */
    'etg.api_call': {
        endpoint: string;
        status: 'ok' | 'error';
        latencyMs: number;
        errorCode: string | null;
        regionId: number | null;
    };

    'api.request': {
        route: string;
        method: string;
        statusCode: number;
        latencyMs: number;
    };

    'auth.signed_in': {
        userId: string;
        isNewUser: boolean;
    };

    /** Links pre-login activity to the account, so the funnel survives sign-in. */
    'identity.alias': {
        anonId: string;
        userId: string;
        isNewUser: boolean;
    };

    'support.ticket_created': {
        ticketId: string;
        partnerOrderId: string | null;
        category: string | null;
    };

    'support.ticket_resolved': {
        ticketId: string;
        hoursToResolve: number;
    };
};

type EventMap = ClientEventMap & ServerEventMap;

type EventName = keyof EventMap;

type EventProps<N extends EventName> = EventMap[N];

// Derived from the maps above so the lists cannot drift from the types.
const CLIENT_EVENT_NAMES = [
    'search.submitted',
    'search.results_returned',
    'search.filter_applied',
    'search.sort_changed',
    'search.page_viewed',
    'search.result_clicked',
    'hotel.viewed',
    'hotel.rooms_returned',
    'hotel.room_selected',
    'checkout.started',
    'checkout.guest_details_completed',
    'checkout.prebook_result',
    'checkout.abandoned',
    'cms.section_viewed',
    'cms.item_clicked',
    'screen.viewed',
    'app.error'
] as const satisfies ReadonlyArray<keyof ClientEventMap>;

const SERVER_EVENT_NAMES = [
    'payment.initiated',
    'payment.captured',
    'payment.failed',
    'payment.refunded',
    'booking.order_placed',
    'booking.order_failed',
    'booking.cancelled',
    'etg.api_call',
    'api.request',
    'auth.signed_in',
    'identity.alias',
    'support.ticket_created',
    'support.ticket_resolved'
] as const satisfies ReadonlyArray<keyof ServerEventMap>;

const ALL_EVENT_NAMES = [...CLIENT_EVENT_NAMES, ...SERVER_EVENT_NAMES] as const;

function isEventName(value: string): value is EventName {
    return (ALL_EVENT_NAMES as ReadonlyArray<string>).includes(value);
}

/** A client batch cannot claim a server event. */
function isAllowedFromSource(name: EventName, source: EventSource): boolean {
    return source === 'server'
        ? true
        : (CLIENT_EVENT_NAMES as ReadonlyArray<string>).includes(name);
}

export {
    ALL_EVENT_NAMES,
    CLIENT_EVENT_NAMES,
    SERVER_EVENT_NAMES,
    isAllowedFromSource,
    isEventName
};

export type {
    ClientEventMap,
    EventMap,
    EventName,
    EventProps,
    EventSource,
    Money,
    Platform,
    ServerEventMap
};
